/**
 * Chain presentation: flat colored discs with the network's actual mark
 * (white, drawn inline as SVG) where the mark is unambiguous — monogram
 * fallback elsewhere. Same visual language as CurrencyBadge so a wallet's
 * chain and its held assets read as one consistent icon system.
 */
export const CHAIN_META: Record<string, { name: string; color: string; glyph: string }> = {
  ethereum: { name: "Ethereum", color: "#627EEA", glyph: "Ξ" },
  base: { name: "Base", color: "#0052FF", glyph: "B" },
  polygon: { name: "Polygon", color: "#8247E5", glyph: "P" },
  solana: { name: "Solana", color: "#14B88A", glyph: "S" },
  arbitrum: { name: "Arbitrum", color: "#28A0F0", glyph: "A" },
  optimism: { name: "Optimism", color: "#FF0420", glyph: "O" },
  avalanche: { name: "Avalanche", color: "#E84142", glyph: "A" },
  tron: { name: "Tron", color: "#EF0027", glyph: "T" },
  stellar: { name: "Stellar", color: "#7D00FF", glyph: "S" },
  bsc: { name: "BNB Chain", color: "#F0B90B", glyph: "B" },
  bitcoin: { name: "Bitcoin", color: "#F7931A", glyph: "₿" },
};

export function chainName(chain: string): string {
  return CHAIN_META[chain]?.name ?? chain;
}

/** White network marks, drawn on a 24×24 grid, rendered over the chain-colored disc. */
const CHAIN_MARKS: Record<string, React.ReactNode> = {
  // The classic octahedron, split top/bottom with the customary shading.
  ethereum: (
    <>
      <path d="M12 3.2 6.7 12.1l5.3 3.2 5.3-3.2L12 3.2z" fill="#fff" opacity=".9" />
      <path d="M12 3.2v12.1l5.3-3.2L12 3.2z" fill="#fff" opacity=".65" />
      <path d="M6.7 13.2 12 16.4l5.3-3.2L12 20.8 6.7 13.2z" fill="#fff" opacity=".9" />
      <path d="M12 16.4v4.4l5.3-7.6-5.3 3.2z" fill="#fff" opacity=".65" />
    </>
  ),
  // Base: solid disc with the flat notch on the left.
  base: (
    <>
      <circle cx="12" cy="12" r="7.6" fill="#fff" />
      <rect x="2.5" y="10.9" width="10" height="2.2" fill="#0052FF" />
    </>
  ),
  // Solana: three slanted bars (top→right, middle→left, bottom→right).
  solana: (
    <>
      <path d="M7.6 6.4h9.7c.24 0 .47.1.64.27l1.5 1.53c.28.29.08.78-.32.78H9.4c-.24 0-.47-.1-.64-.27l-1.5-1.53c-.28-.29-.08-.78.34-.78z" fill="#fff" />
      <path d="M7.6 15h9.7c.24 0 .47.1.64.27l1.5 1.53c.28.29.08.78-.32.78H9.4c-.24 0-.47-.1-.64-.27l-1.5-1.53c-.28-.29-.08-.78.34-.78z" fill="#fff" />
      <path d="M16.4 10.7H6.7c-.24 0-.47.1-.64.27l-1.5 1.53c-.28.29-.08.78.32.78h9.75c.24 0 .47-.1.64-.27l1.5-1.53c.28-.29.08-.78-.37-.78z" fill="#fff" />
    </>
  ),
};

export function ChainBadge({ chain, size = 20 }: { chain: string; size?: number }) {
  const meta = CHAIN_META[chain];
  const mark = CHAIN_MARKS[chain];
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-medium"
      style={{
        width: size,
        height: size,
        background: meta?.color ?? "var(--c-charcoal)",
        color: "var(--c-white)",
        fontSize: size * 0.5,
        lineHeight: 1,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
      }}
      aria-hidden
    >
      {mark ? (
        <svg width={size * 0.82} height={size * 0.82} viewBox="0 0 24 24">
          {mark}
        </svg>
      ) : (
        (meta?.glyph ?? chain[0]?.toUpperCase())
      )}
    </span>
  );
}
